import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Card } from '../UI'
import { font, FUNNEL_STAGES, LLM_PLATFORMS, LANGUAGES, DEFAULT_LLM_PLATFORM_IDS } from './constants'
import SectionDivider from './SectionDivider'
import FormInput from './FormInput'
import TagInput from './TagInput'
import SearchableDrp from '../common/SearchableDrp'
import ParsedAudiencesPanel from './ParsedAudiencesPanel'
import {
  SKIP_AUDIENCE_SELECTION_STEP,
  extractAudiencesFromList,
  getFirstAudienceId,
  resolveSelectedAudienceId,
  buildGenerateQueriesPayload,
} from './audienceSelection'
import AudienceContinueButton from './AudienceContinueButton'
import { getOneSimulationDetails, saveSimulation } from '../../requests'
import { notifyError, notifySuccess } from '../../utils/helper'
import { toggleRefresh } from '../../_store/reducers/refreshReducer'

function normalizeLanguage(v) {
  if (v == null || v === '') return 'English'
  const s = String(v)
  const found = LANGUAGES.find((l) => l.toLowerCase() === s.toLowerCase())
  return found ?? s
}

function normalizeFunnelId(v) {
  if (v == null || v === '') return 'awareness'
  const raw = String(v).trim()
  const asId = raw.toLowerCase().replace(/\s+/g, '_')
  const byId = FUNNEL_STAGES.find((st) => st.id === asId)
  if (byId) return byId.id
  const byLabel = FUNNEL_STAGES.find((st) => st.label.toLowerCase() === raw.toLowerCase())
  return byLabel?.id ?? 'awareness'
}

function normalizeLlmIds(arr) {
  let source = arr == null ? [] : Array.isArray(arr) ? arr : [arr]
  // llm_platform is stored as a JSON-encoded array (e.g. '["claude","perplexity"]')
  // when multiple platforms were selected; unwrap it before matching against ids/labels.
  if (source.length === 1 && typeof source[0] === 'string') {
    try {
      const parsed = JSON.parse(source[0])
      if (Array.isArray(parsed)) source = parsed
    } catch {
      // not JSON — treat as a single bare platform id/label
    }
  }
  if (source.length === 0) return [...DEFAULT_LLM_PLATFORM_IDS]
  const ids = source
    .map((x) => {
      const s = String(x).trim().toLowerCase().replace('chat-gpt', 'chatgpt')
      const byId = LLM_PLATFORMS.find((p) => p.id === s)
      if (byId) return byId.id
      const byLabel = LLM_PLATFORMS.find((p) => p.label.toLowerCase() === s)
      return byLabel?.id
    })
    .filter(Boolean)
  return ids.length ? ids : [...DEFAULT_LLM_PLATFORM_IDS]
}

function parseCompetitorBrands(raw) {
  if (raw == null) return []
  if (Array.isArray(raw)) return raw.map((x) => String(x).trim()).filter(Boolean)
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw)
      return Array.isArray(parsed) ? parsed.map((x) => String(x).trim()).filter(Boolean) : []
    } catch {
      return raw.split(/[,;]/).map((s) => s.trim()).filter(Boolean)
    }
  }
  return []
}

function extractAudiences(audienceList) {
  return extractAudiencesFromList(audienceList)
}

function mapDetailsToForm(details, fallback = {}) {
  const d = details && typeof details === 'object' ? details : {}
  const fb = fallback && typeof fallback === 'object' ? fallback : {}
  return {
    projectName: d.name ?? fb.name ?? '',
    simulationDescription: d.description ?? fb.simulation_description ?? fb.description ?? '',
    language: normalizeLanguage(d.language ?? fb.language),
    funnelStage: normalizeFunnelId(d.funnel_stage ?? fb.funnel?.[0]),
    llmPlatforms: normalizeLlmIds(d.llm_platform ?? fb.llm_platforms ?? fb.llm_platform),
  }
}

function ContinueSimulation({ simulationId, initialData = {}, onBack, onSaveAndGo = () => {} }) {
  const dispatch = useDispatch()
  const context = useSelector((state) => state.context)
  const [loading, setLoading] = useState(true)
  const [submitLoading, setSubmitLoading] = useState(false)
  const [form, setForm] = useState(() => mapDetailsToForm(null, initialData))
  const [competitors, setCompetitors] = useState(() =>
    parseCompetitorBrands(initialData?.competitors ?? initialData?.competitor_brands)
  )
  const [parsedAudiences, setParsedAudiences] = useState(null)
  const [resolvedSimulationId, setResolvedSimulationId] = useState(simulationId ?? initialData?.id ?? null)
  const [selectedParsedAudienceId, setSelectedParsedAudienceId] = useState(null)
  const [personaFileName, setPersonaFileName] = useState('')

  const loadDetails = useCallback(async () => {
    const id = simulationId ?? initialData?.id ?? initialData?.latestSimulationId
    if (id == null) {
      notifyError('Simulation not found')
      onBack?.()
      return
    }
    setLoading(true)
    try {
      const res = await getOneSimulationDetails(id)
      if (res?.status >= 400) {
        notifyError(res?.data?.message ?? 'Failed to load simulation details')
        onBack?.()
        return
      }
      const details = res?.data ?? {}
      setResolvedSimulationId(details.id ?? id)
      setForm(mapDetailsToForm(details, initialData))
      setCompetitors(parseCompetitorBrands(details.competitor_brands ?? initialData?.competitors))
      const audiences = extractAudiences(details.audience_list)
      setParsedAudiences(audiences.length > 0 ? audiences : null)
      const preselected = SKIP_AUDIENCE_SELECTION_STEP
        ? getFirstAudienceId(audiences)
        : resolveSelectedAudienceId(
            audiences,
            details.selected_audience ??
              initialData?.selected_audience ??
              getFirstAudienceId(audiences),
          )
      setSelectedParsedAudienceId(
        preselected != null && preselected !== '' ? String(preselected) : null,
      )
      setPersonaFileName(details.persona_name ?? '')
    } catch (err) {
      notifyError(err?.response?.data?.message ?? 'Failed to load simulation details')
      onBack?.()
    } finally {
      setLoading(false)
    }
  }, [simulationId, initialData, onBack])

  useEffect(() => {
    loadDetails()
  }, [loadDetails])

  const canContinue = useMemo(
    () =>
      Boolean(
        resolvedSimulationId != null &&
          String(resolvedSimulationId).trim() !== '' &&
          resolveSelectedAudienceId(parsedAudiences, selectedParsedAudienceId) &&
          Array.isArray(parsedAudiences) &&
          parsedAudiences.length > 0
      ),
    [resolvedSimulationId, selectedParsedAudienceId, parsedAudiences]
  )

  const handleContinue = async () => {
    if (submitLoading) return
    const effectiveAudience = resolveSelectedAudienceId(
      parsedAudiences,
      selectedParsedAudienceId,
    )
    if (!canContinue || !effectiveAudience) {
      notifyError(
        SKIP_AUDIENCE_SELECTION_STEP
          ? 'Persona is not ready yet. Wait for processing to complete.'
          : 'Select an audience from the list below to continue.',
      )
      return
    }
    setSubmitLoading(true)
    try {
      const res = await saveSimulation(
        buildGenerateQueriesPayload(resolvedSimulationId, {
          isNewSimulation: false,
          selectedAudience: effectiveAudience,
        }),
      )
      if (res?.status < 400) {
        dispatch(toggleRefresh())
        notifySuccess(res?.data?.message ?? 'Query generation started successfully')
        onSaveAndGo?.(resolvedSimulationId)
      } else {
        notifyError(res?.data?.message ?? 'Failed to continue simulation')
      }
    } catch (err) {
      notifyError(err?.response?.data?.message ?? 'Failed to continue simulation')
    } finally {
      setSubmitLoading(false)
    }
  }

  const fieldsDisabled = true

  if (loading) {
    return (
      <div style={{ padding: 48, textAlign: 'center', color: '#999999', fontFamily: font }}>
        Loading simulation…
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            type="button"
            onClick={onBack}
            style={{
              padding: '8px 14px',
              borderRadius: 8,
              border: '1px solid rgba(0,0,0,0.12)',
              background: 'transparent',
              color: '#6b6b6b',
              fontSize: 13,
              cursor: 'pointer',
              fontFamily: font,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            ← Back to Simulations
          </button>
          <div>
            <h1 style={{ fontSize: 24, fontWeight: 700, color: '#363636', margin: 0, fontFamily: font }}>
              Continue Simulation
            </h1>
            <p style={{ fontSize: 13, color: '#999999', margin: '4px 0 0', fontFamily: font }}>
              Review your setup and select an audience to proceed
            </p>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 28, alignItems: 'flex-start' }}>
        <div style={{ flex: '1 1 620px' }}>
          <Card>
            <SectionDivider label="Simulation Details" />
            <FormInput
              label="Simulation Name"
              placeholder="—"
              helpText="Previously entered simulation name."
              icon="📋"
              required
              value={form.projectName}
              onChange={() => {}}
              disabled={fieldsDisabled}
              readOnly
            />
            <FormInput
              label="Simulation description"
              placeholder="—"
              helpText="Optional context provided when this simulation was created."
              icon="📝"
              multiline
              rows={3}
              value={form.simulationDescription}
              onChange={() => {}}
              disabled={fieldsDisabled}
              readOnly
            />
            <FormInput
              label="Project"
              placeholder="—"
              helpText="Read-only — taken from session context."
              icon="📁"
              readOnly
              disabled={fieldsDisabled}
              value={context?.project_name ?? ''}
            />
            <div style={{ marginBottom: 20, cursor: 'not-allowed' }}>
              <label style={{ display: 'block', fontSize: 13, color: '#6b6b6b', marginBottom: 6, fontFamily: font }}>
                Language <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <SearchableDrp
                options={LANGUAGES}
                value={form.language}
                onChange={() => {}}
                placeholder="Select language..."
                icon="🌐"
                disabled={fieldsDisabled}
              />
            </div>

            <SectionDivider label="Competitor Tracking" />
            <TagInput
              label="Competitor Brands"
              placeholder="—"
              helpText="Competitors configured for this simulation."
              required
              tags={competitors}
              onAdd={() => {}}
              onRemove={() => {}}
              disabled={fieldsDisabled}
            />

            <SectionDivider label="Simulation Settings" />
            <div style={{ marginBottom: 24, cursor: 'not-allowed' }}>
              <label style={{ display: 'block', fontSize: 13, color: '#6b6b6b', marginBottom: 10, fontFamily: font }}>
                Funnel Stage <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                {FUNNEL_STAGES.map((stage) => {
                  const sel = form.funnelStage === stage.id
                  return (
                    <div
                      key={stage.id}
                      style={{
                        padding: '14px 16px',
                        borderRadius: 10,
                        cursor: 'not-allowed',
                        opacity: 0.85,
                        background: sel ? 'rgba(239,68,68,0.08)' : 'rgba(0,0,0,0.03)',
                        border: sel ? '1px solid rgba(239,68,68,0.3)' : '1px solid rgba(0,0,0,0.06)',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <span style={{ fontSize: 16 }}>{stage.icon}</span>
                        <span style={{ fontSize: 13, fontWeight: 600, color: sel ? '#363636' : '#64748b', fontFamily: font }}>
                          {stage.label}
                        </span>
                      </div>
                      <p style={{ fontSize: 11, color: '#999999', margin: 0, lineHeight: 1.4, fontFamily: font }}>
                        {stage.desc}
                      </p>
                    </div>
                  )
                })}
              </div>
            </div>

            <div style={{ marginBottom: 28, cursor: 'not-allowed' }}>
              <label style={{ display: 'block', fontSize: 13, color: '#6b6b6b', marginBottom: 10, fontFamily: font }}>
                LLM Platforms <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10 }}>
                {LLM_PLATFORMS.map((p) => {
                  const sel = form.llmPlatforms.includes(p.id)
                  return (
                    <div
                      key={p.id}
                      style={{
                        padding: '14px 12px',
                        borderRadius: 10,
                        cursor: 'not-allowed',
                        background: sel ? 'rgba(239,68,68,0.08)' : 'rgba(0,0,0,0.03)',
                        border: sel ? '1px solid rgba(239,68,68,0.3)' : '1px solid rgba(0,0,0,0.06)',
                        opacity: fieldsDisabled ? 0.85 : p.available ? 1 : 0.4,
                        textAlign: 'center',
                      }}
                    >
                      <div
                        style={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          background: p.color,
                          margin: '0 auto 8px',
                          opacity: sel ? 1 : 0.5,
                        }}
                      />
                      <div style={{ fontSize: 13, fontWeight: 600, color: sel ? '#363636' : '#64748b', fontFamily: font }}>
                        {p.label}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>

            <SectionDivider label="Persona" />
            <div style={{ marginBottom: 20 }}>
              {personaFileName ? (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '12px 16px',
                    borderRadius: 8,
                    background: 'rgba(0,0,0,0.03)',
                    border: '1px solid rgba(0,0,0,0.06)',
                    marginBottom: 14,
                  }}
                >
                  <span style={{ fontSize: 20 }}>📄</span>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#363636', fontFamily: font }}>{personaFileName}</div>
                    <div style={{ fontSize: 11, color: '#999999', fontFamily: font }}>
                      {SKIP_AUDIENCE_SELECTION_STEP
                        ? 'Persona ready — click Continue to generate queries'
                        : 'Persona processed — audiences listed below'}
                    </div>
                  </div>
                </div>
              ) : (
                <p style={{ fontSize: 13, color: '#64748b', fontFamily: font, marginBottom: 14 }}>
                  {SKIP_AUDIENCE_SELECTION_STEP
                    ? 'Persona is ready. Click Continue to start query generation.'
                    : 'Audiences generated for this simulation. Select one to continue.'}
                </p>
              )}

              {SKIP_AUDIENCE_SELECTION_STEP ? (
                canContinue ? (
                  <AudienceContinueButton
                    onClick={handleContinue}
                    loading={submitLoading}
                    disabled={submitLoading}
                  />
                ) : (
                  <p style={{ fontSize: 13, color: '#64748b', fontFamily: font }}>
                    Waiting for persona processing to complete…
                  </p>
                )
              ) : Array.isArray(parsedAudiences) && parsedAudiences.length > 0 ? (
                <ParsedAudiencesPanel
                  audiences={parsedAudiences}
                  selectedAudienceId={selectedParsedAudienceId}
                  onSelectAudience={setSelectedParsedAudienceId}
                />
              ) : (
                <p style={{ fontSize: 13, color: '#ef4444', fontFamily: font }}>
                  No audiences found for this simulation. Please wait for processing to complete or create a new simulation.
                </p>
              )}
            </div>

            {!SKIP_AUDIENCE_SELECTION_STEP && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <button
                type="button"
                onClick={handleContinue}
                disabled={submitLoading || !canContinue}
                style={{
                  width: '100%',
                  padding: '14px 0',
                  borderRadius: 10,
                  border: 'none',
                  background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                  color: '#fff',
                  fontSize: 15,
                  fontWeight: 600,
                  cursor: submitLoading || !canContinue ? 'not-allowed' : 'pointer',
                  fontFamily: font,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  boxShadow: '0 4px 20px rgba(34,197,94,0.2)',
                  opacity: submitLoading || !canContinue ? 0.7 : 1,
                }}
              >
                {submitLoading ? 'Continuing…' : '▶ Continue Simulation'}
              </button>
            </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  )
}

export default ContinueSimulation
